/** @author Rajkumar Rathod */
import { prefersReducedMotion } from './utils';
import type { ResolvedModal } from './config';
import type { CloseReason } from './types';

/**
 * The overlay lives in a shadow root attached to a single host <div>.
 *
 * Shadow DOM is doing real work here: the host site's stylesheets (Bootstrap resets,
 * `* { box-sizing }`, aggressive `div { margin }` rules, Tailwind preflight) cannot
 * reach inside, and our styles cannot leak out. Inherited properties still cross the
 * boundary, so the panel sets font/color/line-height explicitly.
 */

const STYLES = `
:host {
  all: initial;
  position: fixed;
  inset: 0;
  display: block;
  pointer-events: none;
}
* { box-sizing: border-box; }

.backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  opacity: 0;
  transition: opacity 180ms ease;
  pointer-events: auto;
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
}
.wrap {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Half the old 24px. The panel is the thing worth showing; the gutter only
     has to prove it is a layer above the page. */
  padding: 12px;
  pointer-events: none;
}
.panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: var(--ehrw-width);
  max-width: 100%;
  height: var(--ehrw-height, auto);
  max-height: var(--ehrw-max-height);
  min-height: 220px;
  background: #ffffff;
  color: #0f172a;
  font: 400 15px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  border-radius: 16px;
  box-shadow: 0 24px 64px -12px rgba(15, 23, 42, 0.45), 0 0 0 1px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  pointer-events: auto;
  opacity: 0;
  transform: translateY(8px) scale(0.985);
  transition: opacity 200ms ease, transform 200ms cubic-bezier(0.16, 1, 0.3, 1), height 160ms ease;
}
:host([data-open="true"]) .backdrop { opacity: 1; }
:host([data-open="true"]) .panel { opacity: 1; transform: none; }
:host([data-motion="none"]) .backdrop,
:host([data-motion="none"]) .panel { transition: none; }

iframe {
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  border: 0;
  display: block;
  background: transparent;
  opacity: 0;
  transition: opacity 160ms ease;
}
:host([data-loaded="true"]) iframe { opacity: 1; }

.close {
  position: absolute;
  top: 10px;
  inset-inline-end: 10px;
  z-index: 2;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  color: #334155;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;
}
.close:hover { background: rgba(15, 23, 42, 0.12); color: #0f172a; }
.close:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
.close svg { width: 16px; height: 16px; display: block; }

.status {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: grid;
  place-items: center;
  gap: 12px;
  padding: 32px;
  text-align: center;
  background: #ffffff;
}
:host([data-loaded="true"]) .status[data-kind="loading"] { display: none; }
.status[hidden] { display: none; }

.spinner {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2.5px solid rgba(15, 23, 42, 0.12);
  border-top-color: #2563eb;
  animation: ehrw-spin 700ms linear infinite;
}
@keyframes ehrw-spin { to { transform: rotate(360deg); } }
:host([data-motion="none"]) .spinner { animation-duration: 2s; }

.status-body { max-width: 34ch; }
.status-title { margin: 0 0 4px; font-size: 15px; font-weight: 600; }
.status-text { margin: 0; font-size: 13.5px; color: #64748b; }
.sr {
  position: absolute;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

/* Dark surface so the chrome around the iframe matches a dark embed. */
:host([data-theme="dark"]) .panel { background: #0f172a; color: #e2e8f0; }
:host([data-theme="dark"]) .status { background: #0f172a; }
:host([data-theme="dark"]) .status-text { color: #94a3b8; }
:host([data-theme="dark"]) .close { background: rgba(226, 232, 240, 0.1); color: #cbd5e1; }
:host([data-theme="dark"]) .close:hover { background: rgba(226, 232, 240, 0.18); color: #f8fafc; }

/* Full-screen mode: the panel *is* the viewport, so the gap, the radius and the
   drop shadow all stop making sense. Used by the find-doctor grid, which needs
   the width. 100dvh keeps mobile browser chrome from clipping the bottom. */
:host([data-fullscreen="true"]) .wrap { padding: 0; }
:host([data-fullscreen="true"]) .panel {
  width: 100%;
  height: 100vh;
  max-width: none;
  max-height: none;
  border-radius: 0;
  box-shadow: none;
}
@supports (height: 100dvh) {
  :host([data-fullscreen="true"]) .panel { height: 100dvh; }
}

/* Full-height sheet on phones — a centred card wastes the viewport for a form flow. */
@media (max-width: 640px) {
  .wrap { padding: 0; }
  .panel {
    width: 100%;
    height: 100%;
    max-height: none;
    border-radius: 0;
    transform: translateY(16px);
  }
}
@supports (height: 100dvh) {
  @media (max-width: 640px) {
    .panel { height: 100dvh; }
  }
}
`;

const CLOSE_ICON =
  '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
  '<path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>';

/**
 * How long after mounting the backdrop ignores clicks.
 *
 * The overlay appears under the pointer that summoned it, so double-clicking
 * "New Patient" in the anchored menu sent the second click to a backdrop that
 * did not exist when the first one landed — dismissing the form the patient
 * had just asked for.
 */
const BACKDROP_ARM_MS = 400;

export interface OverlayCallbacks {
  onRequestClose: (reason: CloseReason) => void;
}

/** The two attributes that decide what the framed document is allowed to do. */
export interface FrameOptions {
  theme: string;
  /** `sandbox` attribute value, or `false` to omit the attribute entirely. */
  sandbox: string | false;
  /** `allow` (permissions policy) value, or `false` to delegate nothing. */
  allow: string | false;
}

export class Overlay {
  readonly host: HTMLDivElement;
  readonly iframe: HTMLIFrameElement;

  private root: ShadowRoot;
  private panel: HTMLDivElement;
  private loadingStatus: HTMLDivElement;
  private errorStatus: HTMLDivElement;
  private closeButton: HTMLButtonElement | null = null;
  private opts: ResolvedModal;
  private cb: OverlayCallbacks;

  private previousFocus: Element | null = null;
  /** When the backdrop went up; see {@link BACKDROP_ARM_MS}. */
  private mountedAt = 0;
  private scrollLock: { overflow: string; paddingRight: string } | null = null;
  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.opts.closeOnEsc) {
      e.stopPropagation();
      this.cb.onRequestClose('escape');
    }
  };

  constructor(opts: ResolvedModal, frame: FrameOptions, cb: OverlayCallbacks) {
    this.opts = opts;
    this.cb = cb;

    this.host = document.createElement('div');
    this.host.setAttribute('data-ehr-widget-root', '');
    this.host.style.setProperty('z-index', String(opts.zIndex), 'important');
    this.host.dataset['theme'] = frame.theme;
    if (opts.fullScreen) this.host.dataset['fullscreen'] = 'true';
    if (prefersReducedMotion()) this.host.dataset['motion'] = 'none';

    this.root = this.host.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = STYLES;
    this.root.appendChild(style);

    const backdrop = document.createElement('div');
    backdrop.className = 'backdrop';
    backdrop.addEventListener('click', () => {
      if (!this.opts.closeOnBackdrop) return;
      if (Date.now() - this.mountedAt < BACKDROP_ARM_MS) return;
      this.cb.onRequestClose('backdrop');
    });

    const wrap = document.createElement('div');
    wrap.className = 'wrap';

    this.panel = document.createElement('div');
    this.panel.className = 'panel';
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'true');
    this.panel.setAttribute('aria-label', 'Patient services');
    this.panel.style.setProperty('--ehrw-width', opts.width);
    this.panel.style.setProperty('--ehrw-max-height', opts.maxHeight);

    // Leading focus sentinel: Shift+Tab off the top wraps to the end of the panel.
    const sentinelStart = this.makeSentinel(() => this.focusLast());

    if (opts.showCloseButton) {
      this.closeButton = document.createElement('button');
      this.closeButton.type = 'button';
      this.closeButton.className = 'close';
      this.closeButton.setAttribute('aria-label', 'Close');
      this.closeButton.innerHTML = CLOSE_ICON;
      this.closeButton.addEventListener('click', () => this.cb.onRequestClose('user'));
      this.panel.appendChild(this.closeButton);
    }

    this.iframe = document.createElement('iframe');
    this.iframe.title = 'Patient services';
    this.iframe.setAttribute('referrerpolicy', 'strict-origin');
    this.iframe.setAttribute('loading', 'eager');
    if (frame.allow !== false) this.iframe.setAttribute('allow', frame.allow);
    if (frame.sandbox !== false) this.iframe.setAttribute('sandbox', frame.sandbox);

    this.loadingStatus = this.makeStatus('loading', '', 'Loading…', true);
    this.errorStatus = this.makeStatus(
      'error',
      'Something went wrong',
      'We could not load this form. Please close and try again.',
      false
    );
    this.errorStatus.hidden = true;

    const sentinelEnd = this.makeSentinel(() => this.focusFirst());

    this.panel.append(sentinelStart, this.loadingStatus, this.errorStatus, this.iframe, sentinelEnd);
    wrap.appendChild(this.panel);
    this.root.append(backdrop, wrap);
  }

  private makeSentinel(onFocus: () => void): HTMLSpanElement {
    const el = document.createElement('span');
    el.className = 'sr';
    el.tabIndex = 0;
    el.setAttribute('aria-hidden', 'true');
    el.addEventListener('focus', onFocus);
    return el;
  }

  private makeStatus(kind: string, title: string, text: string, spinner: boolean): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'status';
    el.dataset['kind'] = kind;
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');

    const body = document.createElement('div');
    body.className = 'status-body';
    if (spinner) {
      const s = document.createElement('div');
      s.className = 'spinner';
      s.style.margin = '0 auto 12px';
      body.appendChild(s);
    }
    if (title) {
      const h = document.createElement('p');
      h.className = 'status-title';
      h.textContent = title;
      body.appendChild(h);
    }
    const p = document.createElement('p');
    p.className = 'status-text';
    p.textContent = text;
    body.appendChild(p);

    el.appendChild(body);
    return el;
  }

  private focusFirst(): void {
    (this.closeButton ?? this.iframe).focus({ preventScroll: true });
  }

  private focusLast(): void {
    this.iframe.focus({ preventScroll: true });
  }

  /** Attach to the document, load the embed URL, and take focus. */
  mount(url: string): void {
    this.previousFocus = document.activeElement;
    this.mountedAt = Date.now();
    if (this.opts.lockScroll) this.lock();

    document.body.appendChild(this.host);
    this.iframe.src = url;
    document.addEventListener('keydown', this.onKeyDown, true);

    // Next frame, so the transition has an initial state to animate from.
    requestAnimationFrame(() => {
      this.host.dataset['open'] = 'true';
      this.focusFirst();
    });
  }

  markLoaded(): void {
    this.host.dataset['loaded'] = 'true';
  }

  /** Grow/shrink the panel to the height the embed reported. */
  setContentHeight(height: number): void {
    // A full-screen panel is already the height of the viewport; honouring the
    // embed's reported height here would shrink it back to content size.
    if (this.opts.fullScreen) return;
    if (!this.opts.autoHeight || !Number.isFinite(height) || height <= 0) return;
    // The 24 is `.wrap`'s 12px gutter, top and bottom. It has to match, or the
    // panel is held short of the room the layout actually leaves it and the
    // embed scrolls inside a modal that had space left.
    const clamped = Math.max(220, Math.min(Math.round(height), window.innerHeight - 24));
    this.panel.style.setProperty('--ehrw-height', `${clamped}px`);
  }

  showError(message?: string): void {
    if (message) {
      const text = this.errorStatus.querySelector('.status-text');
      if (text) text.textContent = message;
    }
    this.errorStatus.hidden = false;
    this.host.dataset['loaded'] = 'false';
  }

  /** Reverse of mount(). Waits out the exit transition unless motion is disabled. */
  destroy(): void {
    document.removeEventListener('keydown', this.onKeyDown, true);
    this.host.dataset['open'] = 'false';

    const finish = (): void => {
      this.host.remove();
      // Blank the src so the embed stops any in-flight work before GC.
      this.iframe.src = 'about:blank';
      if (this.scrollLock) this.unlock();
      const prev = this.previousFocus;
      if (prev instanceof HTMLElement && document.contains(prev)) {
        prev.focus({ preventScroll: true });
      }
    };

    if (prefersReducedMotion()) finish();
    else window.setTimeout(finish, 200);
  }

  private lock(): void {
    const body = document.body;
    // Compensate for the scrollbar we are about to remove, or the page shifts sideways.
    const gap = window.innerWidth - document.documentElement.clientWidth;
    this.scrollLock = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
    body.style.overflow = 'hidden';
    if (gap > 0) {
      const current = parseFloat(getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${current + gap}px`;
    }
  }

  private unlock(): void {
    if (!this.scrollLock) return;
    document.body.style.overflow = this.scrollLock.overflow;
    document.body.style.paddingRight = this.scrollLock.paddingRight;
    this.scrollLock = null;
  }
}
